import { useCallback, useContext, useMemo, useState } from 'react';

import { TOAST_STATUS } from '@evinced-private/ui-common';

import { waitForBackendIdle } from 'src/common/helpers/WaitForBackendHelper';
import { NotificationsContext } from 'src/common/providers/notificationsProvider/NotificationsContext';
import { getAccessToken } from 'src/common/services/AuthenticationService';
import { Logger } from 'src/common/services/Logger';

import { APP_TYPE, AppType } from '../shared-modal/constants';

const TRANSIENT_FAIL_MESSAGE = 'Connection issue – still trying';

interface UseProcessingStatusResult {
	processingMessage: string | null;
	startPolling: (message: string, successMessage?: string, errorMessage?: string) => void;
}

export const useProcessingStatus = (
	onSuccess?: () => void,
	appType?: AppType
): UseProcessingStatusResult => {
	const [processingMessage, setProcessingMessage] = useState<string | null>(null);
	const { toast } = useContext(NotificationsContext);

	const apiEndpoint = useMemo(
		() => `${process.env.API_ENDPOINT}/instructions/${appType ?? APP_TYPE.WSDK}/summary`,
		[appType]
	);

	const startPolling = useCallback(
		async (
			message: string,
			successMessage = 'Changes saved successfully',
			errorMessage = 'Failed to process changes'
		) => {
			setProcessingMessage(message);

			try {
				await waitForBackendIdle<{ remaining: number }>(apiEndpoint, {
					isIdle: (data) => data.remaining === 0,
					onProgress: () => {
						setProcessingMessage(`${message}`);
					},
					onError: (err, context) => {
						Logger.warn(`Wait error (${context}):`, err);
						toast({
							announcement: TRANSIENT_FAIL_MESSAGE,
							status: TOAST_STATUS.FAIL,
							show: true
						});
					},
					pollIntervalMs: 2500,
					maxRetries: 7,
					fetchInit: {
						headers: {
							Authorization: `Bearer ${getAccessToken()}`,
							'Content-Type': 'application/json'
						}
					}
				});

				setProcessingMessage(null);
				toast({
					announcement: successMessage,
					status: TOAST_STATUS.SUCCESS,
					show: true
				});
				onSuccess?.();
			} catch (err) {
				Logger.error('Waiting aborted or fatally failed', err);
				setProcessingMessage(null);
				toast({
					announcement: errorMessage,
					status: TOAST_STATUS.FAIL,
					show: true
				});
			}
		},
		[onSuccess, toast, apiEndpoint]
	);

	return { processingMessage, startPolling };
};
